import React, { useRef, useState, useEffect } from "react";
import { Box, Dialog, IconButton, Slider, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";

/* ═══════════════════════════════════════════════════════════════════════════
   VIDEO MODAL — Fullscreen premium video player
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  gold: "#d4a373",
  white: "#ffffff",
};

interface Props {
  open: boolean;
  onClose: () => void;
  src: string;
  title?: string;
}

const VideoModal: React.FC<Props> = ({ open, onClose, src, title }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open && videoRef.current) {
      videoRef.current.play().catch(() => {});
      setPlaying(true);
    }
  }, [open]);

  const scheduleHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setShowControls(true);
    hideTimer.current = setTimeout(() => setShowControls(false), 3000);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play().catch(() => {});
    setPlaying(!playing);
    scheduleHide();
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setProgress(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleSeek = (_: Event, val: number | number[]) => {
    if (!videoRef.current) return;
    const t = val as number;
    videoRef.current.currentTime = t;
    setProgress(t);
  };

  const handleFullscreen = () => {
    if (videoRef.current?.requestFullscreen) videoRef.current.requestFullscreen();
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          maxWidth: "95vw",
          height: "92vh",
          maxHeight: "92vh",
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: T.navy,
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(15,23,42,0.85)", backdropFilter: "blur(12px)" } },
      }}
    >
      <Box
        ref={containerRef}
        onMouseMove={scheduleHide}
        onClick={togglePlay}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#000",
          cursor: showControls ? "default" : "none",
        }}
      >
        <video
          ref={videoRef}
          src={src}
          autoPlay
          muted={muted}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setPlaying(false)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />

        {/* Close button */}
        <IconButton
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          sx={{
            position: "absolute", top: 16, right: 16, zIndex: 10,
            bgcolor: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)",
            opacity: showControls ? 1 : 0, transition: "opacity 0.3s",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseRoundedIcon sx={{ color: T.white, fontSize: 20 }} />
        </IconButton>

        {/* Center play/pause indicator */}
        {!playing && (
          <Box
            sx={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 5, pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: 72, height: 72, borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.12)", backdropFilter: "blur(16px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <PlayArrowRoundedIcon sx={{ color: T.white, fontSize: 36, ml: "3px" }} />
            </Box>
          </Box>
        )}

        {/* Bottom controls */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 10,
            background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)",
            px: 2.5, pb: 2, pt: 5,
            opacity: showControls ? 1 : 0,
            transition: "opacity 0.3s",
          }}
        >
          {/* Title */}
          {title && (
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.white, mb: 1 }}>{title}</Typography>
          )}

          {/* Seek bar */}
          <Slider
            value={progress}
            max={duration || 1}
            onChange={handleSeek}
            size="small"
            sx={{
              color: T.gold,
              height: 3,
              "& .MuiSlider-thumb": { width: 12, height: 12, "&:hover": { boxShadow: `0 0 0 6px rgba(212,163,115,0.2)` } },
              "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: -0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={togglePlay} sx={{ color: T.white, p: 0.5 }}>
                {playing ? <PauseRoundedIcon sx={{ fontSize: 20 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 20 }} />}
              </IconButton>
              <IconButton onClick={() => setMuted(!muted)} sx={{ color: T.white, p: 0.5 }}>
                {muted ? <VolumeOffRoundedIcon sx={{ fontSize: 18 }} /> : <VolumeUpRoundedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontVariantNumeric: "tabular-nums" }}>
                {fmt(progress)} / {fmt(duration)}
              </Typography>
            </Box>
            <IconButton onClick={handleFullscreen} sx={{ color: T.white, p: 0.5 }}>
              <FullscreenRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default VideoModal;
